import { useEffect, useState } from "react";
import { MdInsights } from "react-icons/md";

import {
  Badge,
  Box,
  Flex,
  Grid,
  HStack,
  InfoBox,
  KpiTile,
  Progress,
  Select,
  Table,
  Text,
  VStack,
} from "components/ui";
import { ChartSkeleton } from "components/charts/ChartSkeleton";
import { useAiUsage } from "hooks/useAiUsage";
import { useTranslation } from "hooks/useTranslation";
import {
  AiAllowanceStanding,
  AiCostPlaneValue,
  AiReportRange,
  AiUsageBreakdownItem,
  AiUsageFilter,
  AiUsageJobItem,
} from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

import {
  allowanceTone,
  formatPercent,
  formatUsd,
  formatWindow,
  sourceLabelKey,
  UsageBreakdownKind,
  usageRowLabel,
} from "./usageFormat";

export type UsageSection =
  | "allowance"
  | "byCustomerCompany"
  | "byCustomer"
  | "bySource"
  | "byWorkflow"
  | "byAutonomousAgent"
  | "topJobs";

const ALL_SECTIONS: UsageSection[] = [
  "allowance",
  "byCustomerCompany",
  "byCustomer",
  "bySource",
  "byWorkflow",
  "byAutonomousAgent",
  "topJobs",
];

/** The server clamps to 1–90; 0 is "this calendar month". */
const WINDOWS = [0, 7, 30, 90];

interface UsagePanelProps {
  /** Bumped by the page's Refresh button. Re-runs the query without remounting the panel. */
  refreshToken?: number;
  /** Narrows every table (and picks the allowance standing) to one customer company or customer. */
  filter?: AiUsageFilter;
  /** Which sections to show; the AI Gateway tab shows all of them. */
  sections?: UsageSection[];
  /** Offer the plane picker. Only the AI Gateway tab does — a customer's page is tenant-plane by definition. */
  showPlane?: boolean;
  /** Leave out the subtitle and shrink the KPI row — for a section inside another page. */
  compact?: boolean;
  /** Drop the KPI row entirely — Dashboard → AI Spend shows the same four tiles in its Spend card. */
  showKpis?: boolean;
  /** The Dashboard's shared date range; replaces the panel's own Period select when given. */
  range?: AiReportRange;
  /** Controlled plane — the page owns the Usage select; the panel hides its own. */
  plane?: AiCostPlaneValue;
  /** Reports the query state so the host can show a refresh spinner in its own card header. */
  onLoadingChange?: (loading: boolean) => void;
}

const formatDay = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const toneToInfoVariant = (
  tone: ReturnType<typeof allowanceTone>,
): "info" | "success" | "warning" | "error" => {
  switch (tone) {
    case "exhausted":
      return "error";
    case "warning":
      return "warning";
    case "ok":
      return "success";
    default:
      return "info";
  }
};

/**
 * The allowance standing (WP-70): how much of the enforced budget that applies to the scope shown
 * is used and when it resets. Renders nothing when no hard limit applies — the default — so the
 * page carries no notice about a cap that does not exist. Nothing here caps spend by itself;
 * enforcement lives on the budget, this is the read-out.
 */
export const AllowanceCard = ({
  standing,
}: {
  standing: AiAllowanceStanding | null;
}) => {
  const { t } = useTranslation();
  if (!standing || !standing.enforced) return null;
  const tone = allowanceTone(standing);
  return (
    <InfoBox variant={toneToInfoVariant(tone)}>
      <VStack align="stretch" gap={2}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <Text fontSize="sm" fontWeight="semibold">
            {t("app.aiGateway.usage.allowance.title")}
          </Text>
          <Badge
            colorPalette={
              tone === "exhausted"
                ? "red"
                : tone === "warning"
                  ? "yellow"
                  : "green"
            }
            size="sm"
          >
            {tone === "exhausted"
              ? t("app.aiGateway.usage.allowance.exhausted")
              : t("app.aiGateway.usage.allowance.enforced")}
          </Badge>
        </Flex>
        <Text fontSize="sm">
          {t("app.aiGateway.usage.allowance.standing", {
            name: standing.budgetName ?? "",
            spent: formatUsd(standing.spentUsd),
            amount: formatUsd(standing.amountUsd),
            percent: formatPercent(standing.percentUsed),
          })}
        </Text>
        <Progress value={Math.min(100, standing.percentUsed)} size="sm" />
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.usage.allowance.resets", {
            date: formatDay(standing.periodEnd),
          })}
        </Text>
      </VStack>
    </InfoBox>
  );
};

/**
 * Usage attribution (WP-70) — who spent the tokens and how: per customer company, customer,
 * source, workflow, autonomous agent and job. Reused by the customer-company and customer detail
 * pages with a filter and a subset of sections.
 */
const UsagePanel = ({
  refreshToken = 0,
  filter,
  sections = ALL_SECTIONS,
  showPlane = false,
  compact = false,
  showKpis = true,
  range,
  plane: controlledPlane,
  onLoadingChange,
}: UsagePanelProps) => {
  const { t } = useTranslation();
  const [days, setDays] = useState(WINDOWS[2]);
  const [ownPlane, setPlane] = useState<AiCostPlaneValue>(
    AiCostPlaneValue.Tenant,
  );
  const plane = controlledPlane ?? ownPlane;
  const { summary, loading } = useAiUsage(
    days,
    plane,
    filter,
    refreshToken,
    range,
  );
  const show = (section: UsageSection) => sections.includes(section);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const windowItems = WINDOWS.map((value) => ({
    value: String(value),
    label:
      value === 0
        ? t("app.aiGateway.usage.window.thisMonth")
        : t("app.aiGateway.costs.window.option", { days: value }),
  }));

  const planeItems = [
    {
      value: String(AiCostPlaneValue.Tenant),
      label: t("app.aiGateway.costs.plane.tenant"),
    },
    {
      value: String(AiCostPlaneValue.Platform),
      label: t("app.aiGateway.costs.plane.platform"),
    },
    {
      value: String(AiCostPlaneValue.All),
      label: t("app.aiGateway.costs.plane.all"),
    },
  ];

  const windowLabel = range
    ? formatWindow(
        summary?.from ?? range.startDate,
        summary?.to ?? range.endDate,
      )
    : (summary?.days ?? days) === 0
      ? t("app.aiGateway.usage.window.thisMonth")
      : t("app.aiGateway.costs.kpi.window", { days: summary?.days ?? days });

  const renderBreakdown = (
    kind: UsageBreakdownKind,
    titleKey: string,
    nameHeaderKey: string,
    items: AiUsageBreakdownItem[],
  ) => (
    <VStack align="stretch" gap={2} key={kind}>
      <Text fontSize="sm" fontWeight="semibold">
        {t(titleKey)}
      </Text>
      {items.length === 0 ? (
        <Text fontSize="sm" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.costs.noBreakdown")}
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline" minW="620px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>{t(nameHeaderKey)}</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.calls")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.inputTokens")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.outputTokens")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.cost")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={`${kind}-${item.key ?? "none"}`}>
                  <Table.Cell>
                    <VStack align="start" gap={0}>
                      <Text fontSize="sm">{usageRowLabel(item, kind, t)}</Text>
                      {item.unpricedCalls > 0 && (
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {t("app.aiGateway.costs.table.unpricedRow", {
                            count: item.unpricedCalls,
                          })}
                        </Text>
                      )}
                    </VStack>
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {item.calls.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {item.inputTokens.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {item.outputTokens.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {formatUsd(item.costUsd)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VStack>
  );

  const renderJobs = (jobs: AiUsageJobItem[]) => (
    <VStack align="stretch" gap={2}>
      <Text fontSize="sm" fontWeight="semibold">
        {t("app.aiGateway.usage.topJobs")}
      </Text>
      {jobs.length === 0 ? (
        <Text fontSize="sm" color={getThemeColor("text.secondary")}>
          {t("app.aiGateway.costs.noBreakdown")}
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline" minW="760px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t("app.aiGateway.usage.table.job")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.usage.table.source")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.usage.table.customer")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.aiGateway.usage.table.status")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.calls")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.usage.table.tokens")}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="end">
                  {t("app.aiGateway.costs.table.cost")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {jobs.map((job) => (
                <Table.Row key={job.jobId}>
                  <Table.Cell>
                    <VStack align="start" gap={0}>
                      <Text fontSize="sm" fontFamily="mono">
                        {job.jobId.slice(0, 8)}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={getThemeColor("text.secondary")}
                      >
                        {formatDay(job.createdAt)}
                      </Text>
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <VStack align="start" gap={0}>
                      <Text fontSize="sm">
                        {t(sourceLabelKey(job.sourceKind))}
                      </Text>
                      {job.sourceLabel && (
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {job.sourceLabel}
                        </Text>
                      )}
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <VStack align="start" gap={0}>
                      <Text fontSize="sm">
                        {job.customerLabel ??
                          t("app.aiGateway.usage.unattributed")}
                      </Text>
                      {job.customerCompanyLabel && (
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {job.customerCompanyLabel}
                        </Text>
                      )}
                    </VStack>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      size="sm"
                      colorPalette={
                        job.status === "Paused"
                          ? "yellow"
                          : job.status === "Failed"
                            ? "red"
                            : "gray"
                      }
                    >
                      {job.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {job.calls.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {(job.inputTokens + job.outputTokens).toLocaleString()}
                  </Table.Cell>
                  <Table.Cell textAlign="end">
                    {formatUsd(job.costUsd)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </VStack>
  );

  return (
    <VStack align="stretch" gap={5}>
      <Flex justify="space-between" align="flex-end" wrap="wrap" gap={3}>
        {!compact ? (
          <Text
            fontSize="sm"
            color={getThemeColor("text.secondary")}
            maxW="560px"
          >
            {t("app.aiGateway.usage.subtitle")}
          </Text>
        ) : (
          <Box />
        )}
        <HStack gap={3} wrap="wrap">
          {!range && (
            <Select
              label={t("app.aiGateway.costs.window.label")}
              items={windowItems}
              value={[String(days)]}
              onValueChange={(d: { value: string[] }) =>
                setDays(Number(d.value[0]))
              }
              size="sm"
              w="170px"
            />
          )}
          {showPlane && controlledPlane === undefined && (
            <Select
              label={t("app.aiGateway.costs.plane.label")}
              items={planeItems}
              value={[String(plane)]}
              onValueChange={(d: { value: string[] }) =>
                setPlane(Number(d.value[0]) as AiCostPlaneValue)
              }
              size="sm"
              w="200px"
            />
          )}
        </HStack>
      </Flex>

      {show("allowance") && (
        <AllowanceCard standing={summary?.allowance ?? null} />
      )}

      {showKpis && (
        <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
          <KpiTile
            label={t("app.aiGateway.costs.kpi.totalCost")}
            value={summary ? formatUsd(summary.totalCostUsd) : "—"}
            sublabel={windowLabel}
          />
          <KpiTile
            label={t("app.aiGateway.costs.kpi.calls")}
            value={summary ? summary.totalCalls.toLocaleString() : "—"}
            sublabel={windowLabel}
          />
          <KpiTile
            tone="flat"
            label={t("app.aiGateway.costs.kpi.inputTokens")}
            value={summary ? summary.totalInputTokens.toLocaleString() : "—"}
            sublabel={windowLabel}
          />
          <KpiTile
            tone="flat"
            label={t("app.aiGateway.costs.kpi.outputTokens")}
            value={summary ? summary.totalOutputTokens.toLocaleString() : "—"}
            sublabel={windowLabel}
          />
        </Grid>
      )}

      {summary && summary.unpricedCalls > 0 && (
        <InfoBox variant="warning">
          <Text fontSize="sm">
            {t("app.aiGateway.costs.unpricedNotice", {
              count: summary.unpricedCalls,
            })}
          </Text>
        </InfoBox>
      )}

      {!summary && loading && <ChartSkeleton height={220} />}

      {!summary && !loading && (
        <VStack gap={3} py={10}>
          <Box color={getThemeColor("text.secondary")} fontSize="3xl">
            <MdInsights />
          </Box>
          <Text color={getThemeColor("text.secondary")} textAlign="center">
            {t("app.aiGateway.costs.empty")}
          </Text>
        </VStack>
      )}

      {summary && (
        <>
          {show("byCustomerCompany") &&
            renderBreakdown(
              "customerCompany",
              "app.aiGateway.usage.byCustomerCompany",
              "app.aiGateway.usage.table.customerCompany",
              summary.byCustomerCompany,
            )}
          {show("byCustomer") &&
            renderBreakdown(
              "customer",
              "app.aiGateway.usage.byCustomer",
              "app.aiGateway.usage.table.customer",
              summary.byCustomer,
            )}
          {show("bySource") &&
            renderBreakdown(
              "source",
              "app.aiGateway.usage.bySource",
              "app.aiGateway.usage.table.source",
              summary.bySource,
            )}
          {show("byWorkflow") &&
            renderBreakdown(
              "workflow",
              "app.aiGateway.usage.byWorkflow",
              "app.aiGateway.usage.table.workflow",
              summary.byWorkflow,
            )}
          {show("byAutonomousAgent") &&
            renderBreakdown(
              "autonomousAgent",
              "app.aiGateway.usage.byAutonomousAgent",
              "app.aiGateway.usage.table.autonomousAgent",
              summary.byAutonomousAgent,
            )}
          {show("topJobs") && renderJobs(summary.topJobs)}
        </>
      )}
    </VStack>
  );
};

export default UsagePanel;
